const URL_WITH_PROTOCOL_REGEX = /^https?:\/\//i;
const DOMAIN_LIKE_REGEX = /^(?:www\.)?[^\s.]+\.[^\s]{2,}(?:[/?#].*)?$/i;
const USERNAME_LIKE_REGEX = /^@[A-Za-z0-9._-]{2,}$/;
import { MAX_HOST_ALLOWLIST, normalizeMaxInviteUrl, normalizeTelegramUsernameValue, normalizeVkToUrl, normalizeWhatsappE164Ru, } from './validation.js';
export const EXTERNAL_CONTACT_PLATFORM_META = {
    vk: { label: 'VK', icon: 'vk', placeholder: 'https://vk.com/...' },
    telegram: { label: 'Telegram', icon: 'telegram', placeholder: '@username or https://t.me/...' },
    whatsapp: { label: 'WhatsApp', icon: 'whatsapp', placeholder: '+79991234567 or https://wa.me/...' },
    viber: { label: 'Viber', icon: 'viber', placeholder: 'https://invite.viber.com/...' },
    max: { label: 'MAX', icon: 'max', placeholder: 'https://max.ru/...' },
    instagram: { label: 'Instagram', icon: 'instagram', placeholder: 'https://instagram.com/...' },
    facebook: { label: 'Facebook', icon: 'facebook', placeholder: 'https://facebook.com/...' },
    tiktok: { label: 'TikTok', icon: 'tiktok', placeholder: 'https://tiktok.com/@...' },
    youtube: { label: 'YouTube', icon: 'youtube', placeholder: 'https://youtube.com/...' },
    google_maps: { label: 'Google Maps', icon: 'googleMaps', placeholder: 'https://maps.google.com/...' },
    yandex_maps: { label: 'Yandex Maps', icon: 'yandexMaps', placeholder: 'https://yandex.ru/maps/...' },
    two_gis: { label: '2GIS', icon: 'twoGis', placeholder: 'https://2gis.ru/...' },
    avito: { label: 'Avito', icon: 'avito', placeholder: 'https://avito.ru/...' },
};
export function getExternalContactPlatformMeta(platform) {
    return EXTERNAL_CONTACT_PLATFORM_META[platform];
}
export function getExternalContactKind(platform) {
    if (platform === 'telegram' || platform === 'whatsapp' || platform === 'viber' || platform === 'max') {
        return 'messenger';
    }
    return 'social';
}
export function getExternalContactDisplayLabel(contact) {
    return (contact.label || '').trim() || getExternalContactPlatformMeta(contact.platform).label;
}
function normalizeGenericUrlishValue(input) {
    const trimmed = input.trim();
    if (!trimmed)
        return null;
    if (/\s/.test(trimmed) && !URL_WITH_PROTOCOL_REGEX.test(trimmed))
        return null;
    if (URL_WITH_PROTOCOL_REGEX.test(trimmed))
        return trimmed;
    if (USERNAME_LIKE_REGEX.test(trimmed))
        return trimmed;
    if (DOMAIN_LIKE_REGEX.test(trimmed))
        return `https://${trimmed.replace(/^www\./i, 'www.')}`;
    return /^\S+$/.test(trimmed) ? trimmed : null;
}
export function buildExternalContactHref(contact) {
    const value = (contact.value || '').trim();
    if (!value)
        return null;
    switch (contact.platform) {
        case 'telegram': {
            const username = value.replace(/^@+/, '').trim();
            return username ? `https://t.me/${username}` : null;
        }
        case 'whatsapp': {
            const digits = value.replace(/\D/g, '');
            return digits ? `https://wa.me/${digits}` : null;
        }
        case 'vk':
        case 'instagram':
        case 'facebook':
        case 'tiktok':
        case 'youtube':
        case 'google_maps':
        case 'yandex_maps':
        case 'two_gis':
        case 'avito':
        case 'viber':
        case 'max':
            return normalizeGenericUrlishValue(value);
        default:
            return null;
    }
}
export function normalizeExternalContact(contact, expectedKind) {
    const platform = contact.platform;
    const platformKind = getExternalContactKind(platform);
    const effectiveKind = expectedKind ?? platformKind;
    const kindMatches = effectiveKind === platformKind ||
        (platform === 'vk' && effectiveKind === 'messenger') ||
        (platform === 'telegram' && effectiveKind === 'social');
    if (!kindMatches)
        return null;
    let value = null;
    const rawValue = (contact.value || '').trim();
    if (!rawValue)
        return null;
    switch (platform) {
        case 'vk': {
            value = normalizeVkToUrl(contact.value)?.url ?? null;
            break;
        }
        case 'telegram': {
            const normalized = normalizeTelegramUsernameValue(contact.value);
            value = normalized ? `@${normalized.username}` : null;
            break;
        }
        case 'whatsapp': {
            const normalized = normalizeWhatsappE164Ru(contact.value);
            value = normalized?.e164 ?? null;
            break;
        }
        case 'max': {
            value = normalizeMaxInviteUrl(contact.value, { allowlist: MAX_HOST_ALLOWLIST })?.url ?? null;
            break;
        }
        default:
            value = normalizeGenericUrlishValue(contact.value);
            break;
    }
    if (!value)
        return null;
    return {
        platform,
        value,
    };
}
export function normalizeExternalContactList(contacts, kind) {
    if (!Array.isArray(contacts))
        return [];
    return contacts
        .map((contact) => normalizeExternalContact(contact, kind))
        .filter((contact) => !!contact);
}
export function parseExternalContacts(settings) {
    const source = settings && typeof settings === 'object' ? settings : {};
    const socialLinks = normalizeExternalContactList(source.socialLinks, 'social');
    const messengerLinks = normalizeExternalContactList(source.messengerLinks, 'messenger');
    if (socialLinks.length > 0 || messengerLinks.length > 0) {
        return { socialLinks, messengerLinks };
    }
    const legacySocials = [];
    const legacyMessengers = [];
    if (typeof source.vkUrl === 'string' && source.vkUrl.trim()) {
        legacySocials.push({ platform: 'vk', value: source.vkUrl.trim() });
    }
    if (typeof source.telegram === 'string' && source.telegram.trim()) {
        legacyMessengers.push({ platform: 'telegram', value: source.telegram.trim().startsWith('@') ? source.telegram.trim() : `@${source.telegram.trim()}` });
    }
    if (typeof source.whatsapp === 'string' && source.whatsapp.trim()) {
        legacyMessengers.push({ platform: 'whatsapp', value: source.whatsapp.trim() });
    }
    if (typeof source.max === 'string' && source.max.trim()) {
        legacyMessengers.push({ platform: 'max', value: source.max.trim() });
    }
    return {
        socialLinks: normalizeExternalContactList(legacySocials, 'social'),
        messengerLinks: normalizeExternalContactList(legacyMessengers, 'messenger'),
    };
}
export function deriveLegacyExternalContactsFields(contacts) {
    const socialLinks = normalizeExternalContactList(contacts.socialLinks, 'social');
    const messengerLinks = normalizeExternalContactList(contacts.messengerLinks, 'messenger');
    const findValue = (platform, items) => items.find((item) => item.platform === platform)?.value ?? null;
    return {
        vkUrl: findValue('vk', socialLinks),
        telegram: (() => {
            const value = findValue('telegram', messengerLinks);
            return value ? value.replace(/^@+/, '') : null;
        })(),
        whatsapp: findValue('whatsapp', messengerLinks),
        max: findValue('max', messengerLinks),
    };
}
//# sourceMappingURL=externalContacts.js.map